var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * AddEditEventModal - Modal form for creating or editing non-project calendar events.
 * Mirrors the NonProjectPanelComponent form fields: event type, start/end date-time,
 * duration, repeat settings, and comments.
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFocusTrap } from '../../utils/accessibilityUtils';
/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */
const s = {
    backdrop: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        zIndex: 10001,
    },
    panel: {
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: '450px',
        maxWidth: '100vw',
        backgroundColor: '#fff',
        boxShadow: '-4px 0 24px rgba(0,0,0,0.18)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: 10002,
        animation: 'slideInFromRight 0.25s ease-out',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #edebe9',
        backgroundColor: '#faf9f8',
    },
    headerTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#323130',
        margin: 0,
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        fontSize: '22px',
        color: '#605e5c',
        cursor: 'pointer',
        padding: '2px 6px',
        lineHeight: 1,
        borderRadius: '4px',
    },
    body: {
        flex: 1,
        overflowY: 'auto',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
    },
    requiredNote: {
        color: '#a80000',
        fontSize: '13px',
        fontWeight: 600,
        margin: 0,
    },
    label: {
        display: 'block',
        fontSize: '14px',
        fontWeight: 600,
        color: '#323130',
        marginBottom: '4px',
    },
    input: {
        width: '100%',
        padding: '8px 10px',
        fontSize: '14px',
        border: '1px solid #8a8886',
        borderRadius: '4px',
        boxSizing: 'border-box',
        color: '#323130',
        backgroundColor: '#fff',
    },
    select: {
        width: '100%',
        padding: '8px 10px',
        fontSize: '14px',
        border: '1px solid #8a8886',
        borderRadius: '4px',
        boxSizing: 'border-box',
        color: '#323130',
        backgroundColor: '#fff',
        appearance: 'auto',
    },
    textarea: {
        width: '100%',
        padding: '8px 10px',
        fontSize: '14px',
        border: '1px solid #8a8886',
        borderRadius: '4px',
        boxSizing: 'border-box',
        color: '#323130',
        backgroundColor: '#fff',
        resize: 'vertical',
        minHeight: '80px',
        fontFamily: 'inherit',
    },
    row: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end',
    },
    toggleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    toggleLabel: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#323130',
    },
    toggle: {
        width: '40px',
        height: '20px',
        borderRadius: '10px',
        border: '1px solid #8a8886',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background-color 0.2s',
    },
    toggleThumb: {
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        position: 'absolute',
        top: '1px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    },
    durationField: {
        padding: '8px 10px',
        fontSize: '14px',
        backgroundColor: '#f3f2f1',
        border: '1px solid #edebe9',
        borderRadius: '4px',
        color: '#605e5c',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        padding: '12px 20px',
        borderTop: '1px solid #edebe9',
        backgroundColor: '#faf9f8',
    },
    cancelBtn: {
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        border: '1px solid #8a8886',
        backgroundColor: '#fff',
        color: '#323130',
    },
    saveBtn: {
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'pointer',
        border: '1px solid #0078d4',
        backgroundColor: '#0078d4',
        color: '#fff',
    },
    saveBtnDisabled: {
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        fontWeight: 600,
        cursor: 'not-allowed',
        border: '1px solid #c8c6c4',
        backgroundColor: '#c8c6c4',
        color: '#fff',
    },
    warning: {
        fontSize: '13px',
        color: '#a80000',
        backgroundColor: '#fde7e9',
        border: '1px solid #f1bbbc',
        borderRadius: '4px',
        padding: '8px 12px',
    },
};
/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */
const toLocalDateTimeString = (d) => {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const DEFAULT_EVENT_TYPES = ['Vacation', 'Training', 'Bench', 'Other'];
const readCustomProperty = (event, ...keys) => {
    var _a, _b, _c;
    if (!(event === null || event === void 0 ? void 0 : event.customProperties)) {
        return undefined;
    }
    for (const key of keys) {
        const direct = (_a = event.customProperties) === null || _a === void 0 ? void 0 : _a[key];
        if (direct !== undefined && direct !== null && direct !== '') {
            return direct;
        }
        const additional = (_c = (_b = event.customProperties) === null || _b === void 0 ? void 0 : _b.AdditionalProperties) === null || _c === void 0 ? void 0 : _c[key];
        if (additional !== undefined && additional !== null && additional !== '') {
            return additional;
        }
    }
    return undefined;
};
/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */
export const AddEditEventModal = memo(({ isOpen, mode, targetDate, existingEvent, eventTypeOptions, onClose, onSave, }) => {
    const modalRef = useRef(null);
    // Derive initial values
    const initStart = useMemo(() => {
        if (mode === 'edit' && (existingEvent === null || existingEvent === void 0 ? void 0 : existingEvent.startDt)) {
            return new Date(existingEvent.startDt);
        }
        if (targetDate) {
            const d = new Date(targetDate);
            d.setHours(9, 0, 0, 0);
            return d;
        }
        const d = new Date();
        d.setHours(9, 0, 0, 0);
        return d;
    }, [mode, existingEvent, targetDate]);
    const initEnd = useMemo(() => {
        if (mode === 'edit' && (existingEvent === null || existingEvent === void 0 ? void 0 : existingEvent.endDt)) {
            return new Date(existingEvent.endDt);
        }
        if (targetDate) {
            const d = new Date(targetDate);
            d.setHours(17, 0, 0, 0);
            return d;
        }
        const d = new Date();
        d.setHours(17, 0, 0, 0);
        return d;
    }, [mode, existingEvent, targetDate]);
    const types = eventTypeOptions && eventTypeOptions.length > 0 ? eventTypeOptions : DEFAULT_EVENT_TYPES;
    const initEventType = useMemo(() => {
        if (mode === 'edit' && existingEvent) {
            const cp = existingEvent.customProperties;
            return (cp === null || cp === void 0 ? void 0 : cp.AssignmentType) || existingEvent.eventTypeName || types[0] || 'Time Away';
        }
        return types[0] || 'Time Away';
    }, [mode, existingEvent, types]);
    const initComments = useMemo(() => {
        if (mode === 'edit' && (existingEvent === null || existingEvent === void 0 ? void 0 : existingEvent.customProperties)) {
            return readCustomProperty(existingEvent, 'Comments', 'comments') || '';
        }
        return '';
    }, [mode, existingEvent]);
    const initTimezone = useMemo(() => {
        if (mode === 'edit') {
            const eventTimezone = readCustomProperty(existingEvent, 'TimeZone');
            if (typeof eventTimezone === 'string' && eventTimezone.trim()) {
                return eventTimezone;
            }
        }
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        catch (_a) {
            return 'UTC';
        }
    }, [mode, existingEvent]);
    const initRecurrencePattern = useMemo(() => {
        if (mode === 'edit') {
            const isRecurrent = !!readCustomProperty(existingEvent, 'IsRecurrent');
            if (isRecurrent) {
                const recurrencePattern = readCustomProperty(existingEvent, 'RecurrencePattern');
                if (recurrencePattern === 'Daily' || recurrencePattern === 'Weekly' || recurrencePattern === 'Monthly') {
                    return recurrencePattern;
                }
                return 'Daily';
            }
        }
        return 'None';
    }, [mode, existingEvent]);
    const initRecurrenceEndsAfter = useMemo(() => {
        if (mode === 'edit') {
            const value = readCustomProperty(existingEvent, 'RecurrenceEndsAfter');
            if (value !== undefined && value !== null && value !== '') {
                const parsed = Number(value);
                return Number.isFinite(parsed) ? parsed : null;
            }
        }
        return 1;
    }, [mode, existingEvent]);
    const initRecurrenceEndsBy = useMemo(() => {
        if (mode === 'edit') {
            const value = readCustomProperty(existingEvent, 'RecurrenceEndsBy');
            if (typeof value === 'string' && value) {
                const parsed = new Date(value);
                if (!isNaN(parsed.getTime())) {
                    return parsed;
                }
            }
        }
        return null;
    }, [mode, existingEvent]);
    // Derive the recurrence termination mode from the stored record so an existing
    // "Ends after N occurrences" series is not silently rewritten to "Ends by".
    const initRecEndMode = useMemo(() => {
        if (mode === 'edit') {
            const flag = readCustomProperty(existingEvent, 'IsRecurrenceEndDate');
            if (flag !== undefined && flag !== null && flag !== '') {
                const normalized = typeof flag === 'string' ? flag.toLowerCase() : flag;
                // IsRecurrenceEndDate truthy → the series ends on a date ("Ends by").
                return normalized === true || normalized === 'true' || normalized === 1 || normalized === '1'
                    ? 'by'
                    : 'after';
            }
            // No explicit flag: fall back to whichever termination value is populated.
            const endsAfter = readCustomProperty(existingEvent, 'RecurrenceEndsAfter');
            const parsedAfter = Number(endsAfter);
            if (Number.isFinite(parsedAfter) && parsedAfter > 0) {
                return 'after';
            }
        }
        return 'by';
    }, [mode, existingEvent]);
    const initShowAs = useMemo(() => {
        if (mode === 'edit') {
            const showAsValue = readCustomProperty(existingEvent, 'ShowAs');
            if (showAsValue === 'Busy' || showAsValue === 'Free') {
                return showAsValue;
            }
            const isBusy = readCustomProperty(existingEvent, 'IsBusy');
            if (isBusy === false || isBusy === 'false') {
                return 'Free';
            }
        }
        return 'Busy';
    }, [mode, existingEvent]);
    // Form state
    const [eventType, setEventType] = useState(initEventType);
    const [startDt, setStartDt] = useState(initStart);
    const [endDt, setEndDt] = useState(initEnd);
    const [comments, setComments] = useState(initComments);
    const [timezone, setTimezone] = useState(initTimezone);
    const [recurrencePattern, setRecurrencePattern] = useState(initRecurrencePattern);
    const [recurrenceEndsAfter, setRecurrenceEndsAfter] = useState(initRecurrenceEndsAfter);
    const [recurrenceEndsBy, setRecurrenceEndsBy] = useState(initRecurrenceEndsBy);
    const [showAs, setShowAs] = useState(initShowAs);
    const [recEndMode, setRecEndMode] = useState(initRecEndMode);
    const [warning, setWarning] = useState('');
    // Reset form values when modal opens / closes
    useEffect(() => {
        if (isOpen) {
            setEventType(initEventType);
            setStartDt(initStart);
            setEndDt(initEnd);
            setComments(initComments);
            setTimezone(initTimezone);
            setRecurrencePattern(initRecurrencePattern);
            setRecurrenceEndsAfter(initRecurrenceEndsAfter);
            setRecurrenceEndsBy(initRecurrenceEndsBy);
            setRecEndMode(initRecEndMode);
            setShowAs(initShowAs);
            setWarning('');
        }
    }, [
        isOpen,
        initComments,
        initEnd,
        initEventType,
        initRecEndMode,
        initRecurrenceEndsAfter,
        initRecurrenceEndsBy,
        initRecurrencePattern,
        initShowAs,
        initStart,
        initTimezone,
    ]);
    // Escape key
    useEffect(() => {
        if (!isOpen)
            return;
        const h = (e) => { if (e.key === 'Escape')
            onClose(); };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [isOpen, onClose]);
    // Focus trap
    useEffect(() => {
        if (isOpen && modalRef.current) {
            const el = modalRef.current.querySelector('select, input, button');
            el === null || el === void 0 ? void 0 : el.focus();
            return createFocusTrap(modalRef.current);
        }
    }, [isOpen]);
    // Validation
    const validate = useCallback(() => {
        if (startDt >= endDt) {
            setWarning('Start date/time must be before end date/time.');
            return false;
        }
        const isRecurrent = recurrencePattern !== 'None';
        if (isRecurrent && recEndMode === 'after' && (recurrenceEndsAfter === null || recurrenceEndsAfter < 1)) {
            setWarning('Please enter a valid number of occurrences (1 or more).');
            return false;
        }
        if (isRecurrent && recEndMode === 'by' && (!recurrenceEndsBy || isNaN(recurrenceEndsBy.getTime()))) {
            setWarning('Please choose a valid recurrence end date.');
            return false;
        }
        setWarning('');
        return true;
    }, [startDt, endDt, recurrencePattern, recEndMode, recurrenceEndsAfter, recurrenceEndsBy]);
    const handleSave = useCallback(() => {
        if (!validate())
            return;
        const isRecurrent = recurrencePattern !== 'None';
        onSave({
            eventType,
            startDateTime: startDt,
            endDateTime: endDt,
            comments: comments.trim(),
            timezone,
            isRecurrent,
            recurrencePattern,
            // Emit only the selected termination field so the unused one cannot silently
            // override the series semantics: "Ends after" sends the occurrence count and
            // null date; "Ends by" sends the date and null count.
            recurrenceEndsAfter: isRecurrent && recEndMode === 'after'
                ? Math.max(1, recurrenceEndsAfter !== null && recurrenceEndsAfter !== void 0 ? recurrenceEndsAfter : 1)
                : null,
            recurrenceEndsBy: isRecurrent && recEndMode === 'by'
                && recurrenceEndsBy && !isNaN(recurrenceEndsBy.getTime())
                ? recurrenceEndsBy
                : null,
            isRecurrenceEndDate: isRecurrent ? recEndMode === 'by' : false,
            showAs,
            sourceEvent: existingEvent,
        });
    }, [
        validate,
        eventType,
        startDt,
        endDt,
        comments,
        timezone,
        recurrencePattern,
        recEndMode,
        recurrenceEndsAfter,
        recurrenceEndsBy,
        showAs,
        existingEvent,
        onSave,
    ]);
    if (!isOpen)
        return null;
    return (React.createElement("div", { style: s.backdrop, onClick: (e) => { if (e.target === e.currentTarget)
            onClose(); } },
        React.createElement("style", null, `@keyframes slideInFromRight { from { transform: translateX(100%); } to { transform: translateX(0); } }`),
        React.createElement("div", { ref: modalRef, style: s.panel, role: "dialog", "aria-modal": "true", "aria-label": mode === 'add' ? 'Add an Event' : 'Edit Event' },
            React.createElement("div", { style: s.header },
                React.createElement("h2", { style: s.headerTitle }, mode === 'add' ? 'Add an Event' : 'Edit Event'),
                React.createElement("button", { style: s.closeBtn, onClick: onClose, "aria-label": "Close dialog" }, "\u00D7")),
            React.createElement("div", { style: s.body },
                React.createElement("p", { style: s.requiredNote },
                    React.createElement("span", { style: { color: '#a80000' } }, "*"),
                    " Required Field(s)"),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-start" },
                        "From ",
                        React.createElement("span", { style: { color: '#a80000' } }, "*")),
                    React.createElement("input", { id: "aem-start", type: "datetime-local", style: s.input, value: toLocalDateTimeString(startDt), onChange: (e) => setStartDt(new Date(e.target.value)) })),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-end" },
                        "To ",
                        React.createElement("span", { style: { color: '#a80000' } }, "*")),
                    React.createElement("input", { id: "aem-end", type: "datetime-local", style: s.input, value: toLocalDateTimeString(endDt), onChange: (e) => setEndDt(new Date(e.target.value)) })),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-tz" },
                        "Timezone ",
                        React.createElement("span", { style: { color: '#a80000' } }, "*")),
                    React.createElement("input", { id: "aem-tz", type: "text", style: s.input, value: timezone, readOnly: true })),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-event-type" },
                        "Type ",
                        React.createElement("span", { style: { color: '#a80000' } }, "*")),
                    React.createElement("select", { id: "aem-event-type", style: s.select, value: eventType, onChange: (e) => setEventType(e.target.value) },
                        React.createElement("option", { value: "", disabled: true }, "Select"),
                        types.map(t => React.createElement("option", { key: t, value: t }, t)))),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-showas" },
                        "Show As ",
                        React.createElement("span", { style: { color: '#a80000' } }, "*")),
                    React.createElement("select", { id: "aem-showas", style: s.select, value: showAs, onChange: (e) => setShowAs(e.target.value) },
                        React.createElement("option", { value: "Busy" }, "Busy"),
                        React.createElement("option", { value: "Free" }, "Free"))),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-comments" }, "Comments"),
                    React.createElement("textarea", { id: "aem-comments", style: s.textarea, rows: 4, value: comments, placeholder: "", onChange: (e) => setComments(e.target.value) })),
                React.createElement("div", null,
                    React.createElement("label", { style: s.label, htmlFor: "aem-recurrence" }, "Recurrence"),
                    React.createElement("select", { id: "aem-recurrence", style: s.select, value: recurrencePattern, onChange: (e) => {
                            const val = e.target.value;
                            setRecurrencePattern(val);
                            if (val !== 'None') {
                                // Default "Ends by" to event end date when recurrence is first enabled
                                if (!recurrenceEndsBy) {
                                    setRecurrenceEndsBy(endDt);
                                }
                                if (recurrenceEndsAfter === null) {
                                    setRecurrenceEndsAfter(1);
                                }
                            }
                        } },
                        React.createElement("option", { value: "None" }, "None"),
                        React.createElement("option", { value: "Daily" }, "Daily"),
                        React.createElement("option", { value: "Weekly" }, "Weekly"),
                        React.createElement("option", { value: "Monthly" }, "Monthly"))),
                recurrencePattern !== 'None' && (React.createElement("div", { style: {
                        border: '1px solid #edebe9',
                        borderRadius: '4px',
                        padding: '12px',
                        backgroundColor: '#f3f2f1',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    } },
                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } },
                        React.createElement("input", { type: "radio", name: "recEndMode", id: "aem-ends-after", checked: recEndMode === 'after', onChange: () => {
                                setRecEndMode('after');
                                setRecurrenceEndsBy(null);
                                if (recurrenceEndsAfter === null || recurrenceEndsAfter === 0) {
                                    setRecurrenceEndsAfter(1);
                                }
                            }, style: { accentColor: '#0078d4', margin: 0, cursor: 'pointer' } }),
                        React.createElement("label", { htmlFor: "aem-ends-after", style: { fontSize: '14px', color: '#323130', cursor: 'pointer' } }, "Ends after"),
                        React.createElement("input", { type: "radio", name: "recEndMode", id: "aem-ends-by", checked: recEndMode === 'by', onChange: () => {
                                setRecEndMode('by');
                                setRecurrenceEndsAfter(null);
                                if (!recurrenceEndsBy) {
                                    setRecurrenceEndsBy(endDt);
                                }
                            }, style: { accentColor: '#0078d4', margin: 0, cursor: 'pointer', marginLeft: '12px' } }),
                        React.createElement("label", { htmlFor: "aem-ends-by", style: { fontSize: '14px', color: '#323130', cursor: 'pointer' } }, "Ends by")),
                    React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: '8px' } }, recEndMode === 'after' ? (React.createElement(React.Fragment, null,
                        React.createElement("input", { type: "number", min: 1, max: 365, value: recurrenceEndsAfter !== null && recurrenceEndsAfter !== void 0 ? recurrenceEndsAfter : '', onChange: (e) => {
                                const val = e.target.value;
                                if (val === '') {
                                    setRecurrenceEndsAfter(null);
                                }
                                else if (/^\d+$/.test(val)) {
                                    setRecurrenceEndsAfter(parseInt(val, 10));
                                }
                                // Non-numeric characters are silently ignored
                            }, style: Object.assign(Object.assign({}, s.input), { width: '70px', textAlign: 'center' }) }),
                        React.createElement("span", { style: { fontSize: '13px', color: '#a19f9d' } }, "Occurrences"))) : (React.createElement("input", { type: "datetime-local", value: recurrenceEndsBy ? toLocalDateTimeString(recurrenceEndsBy) : toLocalDateTimeString(endDt), onChange: (e) => {
                            const parsed = e.target.value ? new Date(e.target.value) : null;
                            setRecurrenceEndsBy(parsed && !isNaN(parsed.getTime()) ? parsed : null);
                        }, style: s.input }))))),
                warning && React.createElement("div", { role: "alert", style: s.warning }, warning)),
            React.createElement("div", { style: s.footer },
                React.createElement("button", { type: "button", style: s.saveBtn, onClick: handleSave, "aria-label": mode === 'add' ? 'Add event' : 'Update event' }, mode === 'add' ? 'ADD' : 'UPDATE'),
                React.createElement("button", { type: "button", style: s.cancelBtn, onClick: onClose, "aria-label": "Cancel" }, "CANCEL")))));
});
AddEditEventModal.displayName = 'AddEditEventModal';
export default AddEditEventModal;
//# sourceMappingURL=AddEditEventModal.js.map